/**
 * Shared candidate pipeline status flow.
 * Extracted from CandidatesView.vue `computeStatusOption()` so View Job kebab,
 * Dialer, and Call Analytics use the same allowed-next graph.
 *
 * Early-stage Interested / NI / NR / Call Related buttons (also CandidatesView)
 * are a separate set — they apply only while current status is still early.
 */

export function normalizeCandidateStatusKey(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

export function isSamePipelineStatus(left, right) {
  const a = normalizeCandidateStatusKey(left)
  const b = normalizeCandidateStatusKey(right)
  return !!a && a === b
}

const FROM_STATUS_ALIASES = {
  profileshared: 'ProfileShared',
  confirmed: 'Confirmed',
  interview: 'Interview',
  shortlisted: 'Shortlisted',
  offered: 'Offered',
  joined: 'Joined',
  initiated: 'Initiated',
  screenselect: 'ScreenSelect',
  screenreject: 'ScreenReject',
  reviewreject: 'ReviewReject',
  onhold: 'OnHold',
  selected: 'Selected',
  draftoffer: 'Draft Offer',
  l1select: 'L1 Select',
  l2select: 'L2 Select',
  finalselect: 'Final Select',
  inprocess: 'Inprocess',
  notoffered: 'NotOffered',
  offerdrop: 'OfferDrop',
  attrition: 'Attrition',
  earlyattrition: 'Early Attrition',
  left: 'Attrition',
  drop: 'Drop',
}

export function canonicalFromStatus(status) {
  const key = normalizeCandidateStatusKey(status)
  if (!key) return ''
  return FROM_STATUS_ALIASES[key] || String(status || '').trim()
}

function opt(value, text) {
  return { value, text }
}

function optionMatchesStatus(option, status) {
  if (!option || !status) return false
  return isSamePipelineStatus(option.text, status) || isSamePipelineStatus(option.value, status)
}

/** Same early-stage list as CandidatesView `isEarlyStatus` / `CandidateBelongsToSourcerJob`. */
export const EARLY_CALL_OUTCOME_FROM_STATUSES = [
  'UploadedBySourcer',
  'UploadedByRecruiter',
  'AssignedByMassMail',
  'AssignedByATSSourcer',
  'AssignedByATSMassMailer',
  'Not Answered',
  'Not Reachable',
  'Wrong Number',
  'Call Back',
  'Not Interested',
  'Not Relevant',
  'Duplicate',
]

/** Call-related outcomes recruiters pick on Dialer / Call Analytics / View Job buttons. */
export const CALL_OUTCOME_STATUSES = [
  'Call Back',
  'Not Answered',
  'Not Reachable',
  'Wrong Number',
  'Not Interested',
  'Not Relevant',
  'Duplicate',
  'Interested',
]

export function isEarlyCallOutcomeStatus(status) {
  const key = normalizeCandidateStatusKey(status)
  if (!key) return false
  return EARLY_CALL_OUTCOME_FROM_STATUSES.some(item => normalizeCandidateStatusKey(item) === key)
}

/**
 * Follow-up call: ProfileShared or any later pipeline status.
 * Not follow-up: UploadedBy*, Initiated, Interested, Call Back, NI, NA, Duplicate, …
 */
const FOLLOW_UP_PIPELINE_STATUSES = [
  'ProfileShared',
  'Confirmed',
  'ScreenSelect',
  'ScreenReject',
  'Inprocess',
  'OnHold',
  'Interview',
  'L1 Select',
  'L2 Select',
  'Final Select',
  'Shortlisted',
  'Selected',
  'Draft Offer',
  'Offered',
  'NotOffered',
  'OfferDrop',
  'Joined',
  'Attrition',
  'Early Attrition',
  'Rejected',
  'Drop',
  'LOI Issued',
  'Document Submitted',
]

export function isFollowUpPipelineStatus(status) {
  const key = normalizeCandidateStatusKey(status)
  if (!key) return false
  if (isEarlyCallOutcomeStatus(status)) return false
  if (key === 'initiated' || key === 'interested' || key === 'reviewreject') return false
  return FOLLOW_UP_PIPELINE_STATUSES.some(item => normalizeCandidateStatusKey(item) === key)
}

export function isCallOutcomeStatus(status) {
  const key = normalizeCandidateStatusKey(status)
  if (!key) return false
  return CALL_OUTCOME_STATUSES.some(item => normalizeCandidateStatusKey(item) === key)
}

export function statusFlowContextFromJob(job, extras = {}) {
  const positionType = (job && (job.position_type || job.positionType)) || extras.positionType || ''
  const category = (job && job.category) || extras.category || extras.jobCategory || ''
  const payroll = (job && job.payroll) || extras.payroll || ''
  const vertical = (job && job.vertical) || extras.vertical || extras.jobVertical || ''
  const client = (job && (job.client || job.client_name || job.clientName)) || extras.client || ''
  return {
    client,
    positionType,
    category,
    payroll,
    vertical,
    blueCollar: extras.blueCollar
      || (positionType === 'Contractual'
        && category === 'Blue Collar'
        && payroll === 'Sattva Payroll'
        && vertical === 'Infra Industry'),
    hasFinalSelect: !!extras.hasFinalSelect,
    isLastInterviewRoundSelect: !!extras.isLastInterviewRoundSelect,
    isRelianceOfferProcessCompleted: !!extras.isRelianceOfferProcessCompleted,
    isKPMGOfferProcessCompleted: !!extras.isKPMGOfferProcessCompleted,
  }
}

function blueCollarNext(from) {
  if (from === 'ProfileShared' || from === 'Confirmed') {
    return [opt('Interview', 'Interview'), opt('Rejected', 'Rejected')]
  }
  if (from === 'Interview') {
    return [opt('Shortlisted', 'Shortlisted'), opt('Rejected', 'Rejected')]
  }
  if (from === 'Shortlisted') {
    return [opt('OnHold', 'OnHold'), opt('Offered', 'Offered'), opt('Drop', 'Drop')]
  }
  if (from === 'Offered') {
    return [opt('Joined', 'Joined'), opt('Drop', 'Drop')]
  }
  if (from === 'Joined') {
    return [opt('Attrition', 'Attrition')]
  }
  return []
}

function relianceNext(from, ctx) {
  if (from === 'Initiated') {
    return [opt('Review Reject', 'ReviewReject'), opt('Profile Shared', 'ProfileShared')]
  }
  if (from === 'ProfileShared') {
    return [
      opt('Screen Reject', 'ScreenReject'),
      opt('Screen Select', 'ScreenSelect'),
      opt('Duplicate', 'Duplicate'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'ScreenSelect') {
    return [opt('OnHold', 'OnHold'), opt('Interview', 'Interview'), opt('Rejected', 'Rejected')]
  }
  if (from === 'Interview') {
    if (ctx.hasFinalSelect || ctx.isLastInterviewRoundSelect) {
      return [opt('OnHold', 'OnHold'), opt('Selected', 'Selected'), opt('Rejected', 'Rejected')]
    }
    return [opt('OnHold', 'OnHold'), opt('Interview', 'Interview')]
  }
  if (from === 'OnHold') {
    return [opt('Selected', 'Selected'), opt('Rejected', 'Rejected'), opt('Interview', 'Interview')]
  }
  if (from === 'Selected' && !ctx.isRelianceOfferProcessCompleted) {
    return [opt('Draft Offer', 'Draft Offer'), opt('Not Offered', 'NotOffered'), opt('OnHold', 'OnHold')]
  }
  if (from === 'Selected' && ctx.isRelianceOfferProcessCompleted) {
    return [opt('Draft Offer', 'Draft Offer'), opt('Not Offered', 'NotOffered'), opt('Offered', 'Offered')]
  }
  if (from === 'Draft Offer') {
    return [
      opt('Not Offered', 'NotOffered'),
      opt('Offered', 'Offered'),
      opt('Offer Drop', 'OfferDrop'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'Offered') {
    return [
      opt('Draft Offer', 'Draft Offer'),
      opt('Joined', 'Joined'),
      opt('Offer Drop', 'OfferDrop'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'Joined') {
    return [opt('Early Attrition', 'Early Attrition'), opt('Attrition', 'Attrition')]
  }
  return []
}

function defaultNext(from, ctx) {
  if (from === 'Initiated') {
    return [opt('Review Reject', 'ReviewReject'), opt('Profile Shared', 'ProfileShared')]
  }
  if (from === 'ProfileShared') {
    return [
      opt('Screen Reject', 'ScreenReject'),
      opt('Screen Select', 'ScreenSelect'),
      opt('Duplicate', 'Duplicate'),
      opt('Inprocess', 'Inprocess'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'ScreenSelect') {
    return [opt('OnHold', 'OnHold'), opt('Inprocess', 'Inprocess'), opt('Rejected', 'Rejected')]
  }
  if (from === 'L1 Select') {
    return [
      opt('OnHold', 'OnHold'),
      opt('Inprocess', 'Inprocess'),
      opt('Rejected', 'Rejected'),
      opt('Final Select', 'Final Select'),
    ]
  }
  if (from === 'L2 Select') {
    return [
      opt('OnHold', 'OnHold'),
      opt('Shortlisted', 'Shortlisted'),
      opt('Rejected', 'Rejected'),
      opt('Final Select', 'Final Select'),
    ]
  }
  if (from === 'Final Select') {
    return [opt('OnHold', 'OnHold'), opt('Shortlisted', 'Shortlisted'), opt('Rejected', 'Rejected')]
  }
  if (from === 'Inprocess') {
    return [
      opt('OnHold', 'OnHold'),
      opt('Shortlisted', 'Shortlisted'),
      opt('Rejected', 'Rejected'),
      opt('Duplicate', 'Duplicate'),
    ]
  }
  if (from === 'OnHold') {
    return [
      opt('Inprocess', 'Inprocess'),
      opt('Shortlisted', 'Shortlisted'),
      opt('Rejected', 'Rejected'),
      opt('Duplicate', 'Duplicate'),
    ]
  }
  if (from === 'Shortlisted' && ctx.client === 'KPMG RPO' && !ctx.isKPMGOfferProcessCompleted) {
    return [opt('Duplicate', 'Duplicate'), opt('OnHold', 'OnHold'), opt('Not Offered', 'NotOffered')]
  }
  if (from === 'Shortlisted' && ctx.client === 'KPMG RPO' && ctx.isKPMGOfferProcessCompleted) {
    return [
      opt('Offered', 'Offered'),
      opt('Not Offered', 'NotOffered'),
      opt('Duplicate', 'Duplicate'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'Shortlisted') {
    return [
      opt('Offered', 'Offered'),
      opt('Not Offered', 'NotOffered'),
      opt('Duplicate', 'Duplicate'),
      opt('OnHold', 'OnHold'),
    ]
  }
  if (from === 'NotOffered') {
    return [opt('Offered', 'Offered')]
  }
  if (from === 'Offered') {
    return [opt('Joined', 'Joined'), opt('Offer Drop', 'OfferDrop'), opt('Duplicate', 'Duplicate')]
  }
  if (from === 'OfferDrop') {
    return [opt('Joined', 'Joined')]
  }
  if (from === 'Joined') {
    return [
      opt('Offer Drop', 'OfferDrop'),
      opt('Duplicate', 'Duplicate'),
      opt('Early Attrition', 'Early Attrition'),
      opt('Attrition', 'Attrition'),
    ]
  }
  return []
}

/**
 * Allowed next statuses from View Job kebab (`computeStatusOption`).
 * Returns `{ value, text }[]` where `text` is the stored pipeline key.
 */
export function allowedNextStatusOptions(currentStatus, context = {}) {
  const from = canonicalFromStatus(currentStatus)
  if (!from) return []
  const ctx = statusFlowContextFromJob(null, context)
  if (ctx.blueCollar) return blueCollarNext(from)
  if (ctx.client === 'Reliance RPO') return relianceNext(from, ctx)
  return defaultNext(from, ctx)
}

export function allowedNextStatuses(currentStatus, context = {}) {
  return allowedNextStatusOptions(currentStatus, context).map(option => option.text)
}

export function isAllowedNextStatus(currentStatus, nextStatus, context = {}) {
  if (!nextStatus) return false
  if (isSamePipelineStatus(currentStatus, nextStatus)) return true
  return allowedNextStatusOptions(currentStatus, context).some(option => optionMatchesStatus(option, nextStatus))
}

/**
 * Whether Dialer / Call Analytics may write `candidateStatus`.
 * Early statuses: View Job NI / Call Related / Duplicate buttons.
 * Later statuses: kebab `computeStatusOption` only (e.g. Joined → Duplicate allowed,
 * Joined → Not Interested blocked).
 */
export function canTransitionPipelineStatus(currentStatus, nextStatus, context = {}) {
  if (!nextStatus) return false
  if (isFollowUpPipelineStatus(currentStatus)) return false
  if (isSamePipelineStatus(currentStatus, nextStatus)) return true
  if (!normalizeCandidateStatusKey(currentStatus)) return isCallOutcomeStatus(nextStatus)
  if (isEarlyCallOutcomeStatus(currentStatus)) return isCallOutcomeStatus(nextStatus)
  return isAllowedNextStatus(currentStatus, nextStatus, context)
}

export function pipelineTransitionBlockedMessage(currentStatus, nextStatus) {
  const from = String(currentStatus || '').trim() || 'current status'
  const to = String(nextStatus || '').trim() || 'that status'
  return `Cannot change status from ${from} to ${to} (status flow).`
}

export function evaluateCallSurfaceStatusWrite(currentStatus, nextStatus, context = {}) {
  if (isFollowUpPipelineStatus(currentStatus)) {
    return { changeStatus: false, reason: 'follow-up', notesOnly: true }
  }
  if (!nextStatus) {
    return { changeStatus: false, reason: 'none', notesOnly: true }
  }
  if (isSamePipelineStatus(currentStatus, nextStatus)) {
    return { changeStatus: false, reason: 'same', notesOnly: true }
  }
  if (canTransitionPipelineStatus(currentStatus, nextStatus, context)) {
    return { changeStatus: true, reason: 'allowed', notesOnly: false }
  }
  return {
    changeStatus: false,
    reason: 'blocked',
    notesOnly: true,
    message: pipelineTransitionBlockedMessage(currentStatus, nextStatus),
  }
}
